import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Zap, Bell, LogOut, Trash2, X, Sun, Moon, Monitor, Flame, Pencil, Check, Download, UserX } from "lucide-react";
import { Tooltip } from "./Tooltip.tsx";
import { ConfirmDialog } from "./Modal.tsx";
import { ActivityFeed } from "./ActivityFeed.tsx";
import { api, auth } from "../../lib/api.ts";
import { useToast } from "./Toast.tsx";
import { CountryBadge } from "./CountryBadge.tsx";
import { status } from "../../lib/semantics.ts";
import { useAuth } from "../../lib/auth-context.tsx";
import { useTheme } from "../../lib/theme-context.tsx";

interface Alert {
  id: string;
  title: string;
  description: string;
  severity: string;
  recommended_action?: string | null;
}

interface TopbarProps {
  projectName?: string;
  projectId?: string;
  countryCode?: keyof typeof import("../../lib/constants.ts").COUNTRY_META;
  alerts?: Alert[];
  onDismissAlert?: (alertId: string) => void;
  onProjectRenamed?: (newName: string) => void;
}


function fmtDate(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function Topbar({ projectName, projectId, countryCode, alerts = [], onDismissAlert, onProjectRenamed }: TopbarProps) {
  const { user, logOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [showMenu, setShowMenu] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);

  async function handleExportData() {
    setShowMenu(false);
    try {
      const data = await auth.exportMyData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "enereg-my-data.json";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showToast("Your data has been downloaded", "success");
    } catch (e: any) {
      showToast(e?.message || "Could not export your data", "error");
    }
  }
  const [streakCount, setStreakCount] = useState(0);
  const [regStatus, setRegStatus] = useState<{ last_checked_at: string | null; latest_change_at: string | null } | null>(null);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(projectName ?? "");
  const [saving, setSaving] = useState(false);
  const editRef = useRef<HTMLInputElement>(null);

  // Fetch streak (fields updated in last 24h) when project is loaded
  useEffect(() => {
    if (!projectId) { setStreakCount(0); return; }
    let cancelled = false;
    api.getActivityStreak(projectId)
      .then(r => { if (!cancelled) setStreakCount(r.fields_updated_today); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [projectId]);

  // Real per-country regulatory recency for the currency indicator (#20)
  useEffect(() => {
    if (!countryCode) { setRegStatus(null); return; }
    let cancelled = false;
    api.getRegulatoryStatus(countryCode)
      .then(r => { if (!cancelled) setRegStatus(r); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [countryCode]);

  const initials = user
    ? user.name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)
    : "??";

  const alertCount = alerts.length;

  // Deep link: a nudge's "View alerts" action navigates to ?alerts=1 — the
  // drawer is Topbar state, so the URL is consumed (and stripped) here.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("alerts") === "1") {
      setShowAlerts(true);
      params.delete("alerts");
      const rest = params.toString();
      window.history.replaceState(null, "", window.location.pathname + (rest ? `?${rest}` : ""));
    }
  }, []);

  return (
    <header className="h-12 bg-surface border-b border-border flex items-center px-2 sm:px-4 gap-2 sm:gap-3">
      <Link to="/projects" className="flex items-center gap-1 sm:gap-1.5 text-brand font-normal text-[20px] sm:text-[25px] no-underline flex-shrink-0">
        <Zap size={20} className="sm:w-[22px] sm:h-[22px]" />
        <span className="font-bold" style={{ letterSpacing: "-0.02em" }}>enereg</span>
        <span className="beta-badge hidden sm:inline">Beta</span>
      </Link>

      {projectName && (
        <>
          <span className="text-text-tertiary hidden md:inline">/</span>
          {editing ? (
            <form
              className="hidden md:flex items-center gap-1"
              onSubmit={async (e) => {
                e.preventDefault();
                const trimmed = editName.trim();
                if (!trimmed || trimmed === projectName || !projectId) {
                  setEditing(false);
                  setEditName(projectName);
                  return;
                }
                setSaving(true);
                try {
                  await api.renameProject(projectId, trimmed);
                  onProjectRenamed?.(trimmed);
                } catch (err) {
                  console.error("Rename failed:", err);
                  setEditName(projectName);
                } finally {
                  setSaving(false);
                  setEditing(false);
                }
              }}
            >
              <input
                ref={editRef}
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onBlur={() => { if (!saving) { setEditing(false); setEditName(projectName); } }}
                onKeyDown={(e) => { if (e.key === "Escape") { setEditing(false); setEditName(projectName); } }}
                className="text-[16px] font-medium bg-surface-secondary border border-border rounded-lg px-2 py-0.5 outline-none focus:ring-1 focus:ring-brand/30 w-[200px] lg:w-[300px]"
                autoFocus
                disabled={saving}
              />
              <button type="submit" disabled={saving} className="p-1 text-brand hover:bg-brand/10 rounded-lg">
                <Check size={14} />
              </button>
            </form>
          ) : (
            <button
              onClick={() => { setEditName(projectName); setEditing(true); }}
              className="hidden md:flex items-center gap-1.5 text-[16px] text-text-primary font-medium truncate max-w-[200px] lg:max-w-none hover:text-brand transition-colors group"
              title="Click to rename"
            >
              {projectName}
              <Pencil size={12} className="text-text-tertiary opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
            </button>
          )}
          {countryCode && <span className="hidden md:inline"><CountryBadge code={countryCode} /></span>}
        </>
      )}

      <div className="flex-1" />

      {/* Streak chip — shows field activity in last 24h */}
      {streakCount > 0 && (
        <Tooltip text="Fields your team updated in the last 24 hours" position="bottom">
          <div className="hidden sm:flex items-center gap-1 px-2 py-1 bg-brand-light text-brand rounded-full text-[12px] font-medium">
            <Flame size={12} />
            <span>{streakCount} today</span>
          </div>
        </Tooltip>
      )}

      {/* Alert bell — opens the alerts + activity slide-over. Always rendered
          when project-scoped: "what happened since I last looked" matters
          most precisely when there are no alerts screaming for it. */}
      {projectId && (
        <Tooltip
          text={alertCount > 0
            ? `${alertCount} regulatory alert${alertCount !== 1 ? "s" : ""} needing attention`
            : "Alerts and recent activity"}
          position="bottom"
        >
          <button
            onClick={() => setShowAlerts(!showAlerts)}
            aria-label="Alerts and recent activity"
            className={`flex items-center gap-1 px-2 py-1 rounded-full text-[13px] font-medium transition-colors ${
              alertCount > 0
                ? "bg-danger-light text-danger"
                : "text-text-secondary hover:text-text-primary hover:bg-surface-secondary"
            }`}
          >
            <Bell size={12} />
            {alertCount > 0 && <span>{alertCount}</span>}
          </button>
        </Tooltip>
      )}

      {/* User menu */}
      <div className="relative">
        <button
          onClick={() => setShowMenu(!showMenu)}
          className="w-7 h-7 rounded-full bg-brand text-on-solid text-[13px] font-medium flex items-center justify-center hover:bg-brand/90 transition-colors"
        >
          {initials}
        </button>

        {showMenu && (
          <>
            <div className="fixed inset-0 z-[var(--z-dropdown)]" onClick={() => setShowMenu(false)} />
            <div className="absolute right-0 top-9 z-[var(--z-dropdown)] w-52 bg-surface border border-border rounded-xl shadow-lg py-1">
              {user && (
                <div className="px-3 py-2 border-b border-border">
                  <div className="text-[14px] font-medium truncate">{user.name}</div>
                  <div className="text-[12px] text-text-tertiary truncate">{user.email}</div>
                  <div className="text-[12px] text-text-tertiary">{user.org_name} · {user.role}</div>
                </div>
              )}
              <div className="flex items-center gap-1 px-3 py-2 border-b border-border">
                <span className="text-[12px] text-text-tertiary mr-auto">Theme</span>
                {([
                  { value: "light" as const, icon: Sun, label: "Light" },
                  { value: "system" as const, icon: Monitor, label: "System" },
                  { value: "dark" as const, icon: Moon, label: "Dark" },
                ]).map(({ value, icon: Icon, label }) => (
                  <button
                    key={value}
                    title={label}
                    onClick={() => setTheme(value)}
                    className={`p-1.5 rounded-lg transition-colors ${theme === value ? "bg-brand/15 text-brand" : "text-text-tertiary hover:text-text-secondary hover:bg-surface-tertiary"}`}
                  >
                    <Icon size={14} />
                  </button>
                ))}
              </div>
              {projectId && (
                <button
                  onClick={() => { setShowMenu(false); setShowDeleteConfirm(true); }}
                  className="w-full text-left px-3 py-2 text-[14px] text-danger hover:bg-danger-light flex items-center gap-2"
                >
                  <Trash2 size={12} /> Delete project
                </button>
              )}
              <button
                onClick={handleExportData}
                className="w-full text-left px-3 py-2 text-[14px] text-text-secondary hover:bg-surface-secondary flex items-center gap-2"
              >
                <Download size={12} /> Download my data
              </button>
              <button
                onClick={() => { setShowMenu(false); setShowDeleteAccount(true); }}
                className="w-full text-left px-3 py-2 text-[14px] text-danger hover:bg-danger-light flex items-center gap-2"
              >
                <UserX size={12} /> Delete account
              </button>
              <button
                onClick={async () => { await logOut(); setShowMenu(false); }}
                className="w-full text-left px-3 py-2 text-[14px] text-text-secondary hover:bg-surface-secondary flex items-center gap-2 border-t border-border"
              >
                <LogOut size={12} /> Log out
              </button>
            </div>
          </>
        )}
      </div>

      {/* Alert slide-over panel */}
      {showAlerts && (
        <>
          <div className="fixed inset-0 z-[var(--z-overlay)] bg-scrim/10" onClick={() => setShowAlerts(false)} />
          <div className="fixed right-0 top-0 z-[var(--z-overlay)] h-full w-full max-w-sm bg-surface border-l border-border shadow-lg overflow-y-auto">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <h2 className="text-[17px] font-medium">Regulatory Alerts</h2>
              <button onClick={() => setShowAlerts(false)} className="p-1 hover:bg-surface-secondary rounded-lg">
                <X size={16} className="text-text-tertiary" />
              </button>
            </div>

            <div className="px-4 py-3 space-y-3">
              {/* Regulatory currency indicator — real per-country data, honest when sparse (#20) */}
              <div className="flex flex-col gap-0.5 px-3 py-2 bg-surface-secondary rounded-xl text-[13px] text-text-tertiary">
                <span className="flex items-center gap-2">
                  <span className="text-brand">●</span>
                  {regStatus?.latest_change_at
                    ? `Regulatory data reflects changes through ${fmtDate(regStatus.latest_change_at)}`
                    : "Regulatory monitoring active"}
                </span>
                <span>
                  {regStatus?.last_checked_at
                    ? `Sources last checked ${fmtDate(regStatus.last_checked_at)} — verify critical dates with the regulator`
                    : "Verify critical dates with the regulator before filing"}
                </span>
              </div>

              {alerts.length === 0 ? (
                <div className="bg-brand-light rounded-xl px-4 py-3 text-[15px] text-brand">
                  All clear — no regulatory alerts.
                </div>
              ) : (
                alerts.map(alert => {
                  // The alert's OWN severity vocabulary, not a licence status.
                  const sev = status("alert", alert.severity);
                  return (
                    <div key={alert.id} className={`border ${sev.accent ?? "border-info"} ${sev.bg} rounded-2xl px-3.5 py-3`}>
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[12px] font-medium bg-surface ${sev.text}`}>
                              <span aria-hidden>{sev.icon}</span>{sev.label}
                            </span>
                            <span className="text-[15px] font-medium truncate">{alert.title}</span>
                          </div>
                          <p className="text-[13px] text-text-secondary leading-relaxed">{alert.description}</p>
                          {alert.recommended_action && (
                            <div className="mt-2 pt-2 border-t border-border">
                              <span className="text-[11px] font-medium text-text-tertiary uppercase tracking-wider">Recommended</span>
                              <p className="text-[13px] text-text-primary mt-0.5 font-medium">{alert.recommended_action}</p>
                            </div>
                          )}
                        </div>
                        {onDismissAlert && (
                          <button
                            onClick={() => onDismissAlert(alert.id)}
                            className="text-[12px] text-text-tertiary hover:text-text-secondary flex-shrink-0 mt-0.5"
                          >
                            Dismiss
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}

              {/* Since you last looked — everything Enereg did (fields set,
                  documents synced, extractions run), not just what it flags */}
              {projectId && (
                <div className="pt-3 border-t border-border">
                  <h3 className="text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-2">Recent activity</h3>
                  <ActivityFeed key={`af-${alerts.length}`} projectId={projectId} />
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {/* Delete confirmation — the one dialog shell (failure keeps it open with the error) */}
      {showDeleteConfirm && projectId && (
        <ConfirmDialog
          title="Delete project?"
          body={<>This will permanently delete <strong>{projectName}</strong> and all its licenses, fields, documents, and compliance records. This cannot be undone.</>}
          confirmLabel="Delete"
          tone="danger"
          onConfirm={async () => {
            await api.deleteProject(projectId);
            navigate("/projects");
          }}
          onClose={() => setShowDeleteConfirm(false)}
        />
      )}

      {showDeleteAccount && (
        <ConfirmDialog
          title="Delete your account?"
          body={<>This permanently deletes your account and <strong>all</strong> your data — every project, its licenses, fields, documents, and compliance records, plus any waitlist or checkup records tied to your email. This cannot be undone and we cannot recover it. Consider downloading your data first.</>}
          confirmLabel="Delete my account"
          tone="danger"
          onConfirm={async () => {
            try {
              await auth.deleteAccount();
              // Full reset to a clean logged-out state — the session cookie is already cleared server-side.
              window.location.href = "/";
            } catch (e: any) {
              showToast(e?.message || "Could not delete your account", "error");
            }
          }}
          onClose={() => setShowDeleteAccount(false)}
        />
      )}
    </header>
  );
}
